/**
 * Automatic rendering quality (Sprint 2, D5): drops one tier when the median frame time of a 2 s window exceeds the
 * budget; never raises the tier on its own (no oscillation). Pure and allocation-free per frame.
 */

export const QUALITY_TIERS = ['high', 'medium', 'low'] as const;
export type Quality = (typeof QUALITY_TIERS)[number];

/**
 * The tier automatic quality starts in (S4-15, maintainer: "Mobile ist noch sehr langsam"): phones at 'medium' — no
 * multisampling, a quarter-resolution bloom — desktops at 'high'. The monitor below only ever lowers it.
 */
export function startTier(phone: boolean): Quality {
  return phone ? 'medium' : 'high';
}

/**
 * Device pixel ratio cap per tier (S4-15): fill rate is what a phone's GPU runs out of — a Pixel 7 at ratio 2 shades
 * 1.5 million pixels per pass, at 1.5 0.85 million (−44 %). Labels and the HUD are DOM and stay sharp.
 */
export const TIER_MAX_DPR: Readonly<Record<Quality, number>> = { high: 2, medium: 1.5, low: 1.25 };

export function isQuality(value: unknown): value is Quality {
  return typeof value === 'string' && (QUALITY_TIERS as readonly string[]).includes(value);
}

export interface QualityMonitorOptions {
  /** Median frame time above which the tier drops, ms (20 ms = 50 fps; until S4-15 25 ms, which kept 40 fps). */
  budgetMs?: number;
  /** Length of a measurement window, s. */
  windowS?: number;
  /** Ignore this many seconds after start (shader compilation, texture uploads). */
  warmupS?: number;
}

export class QualityMonitor {
  tier: Quality;
  private readonly budgetMs: number;
  private readonly windowS: number;
  private warmup: number;
  private readonly samples = new Float32Array(512);
  private count = 0;
  private elapsed = 0;

  constructor(initial: Quality = 'high', options: QualityMonitorOptions = {}) {
    this.tier = initial;
    this.budgetMs = options.budgetMs ?? 20;
    this.windowS = options.windowS ?? 2;
    this.warmup = options.warmupS ?? 3;
  }

  /** Feeds one frame time (s). Returns the new tier when it dropped this frame, otherwise null. */
  frame(dt: number): Quality | null {
    if (this.warmup > 0) {
      this.warmup -= dt;
      return null;
    }
    if (this.count < this.samples.length) this.samples[this.count++] = dt * 1000;
    this.elapsed += dt;
    if (this.elapsed < this.windowS) return null;
    const median = medianInPlace(this.samples, this.count);
    this.count = 0;
    this.elapsed = 0;
    if (median <= this.budgetMs || this.tier === 'low') return null;
    this.tier = QUALITY_TIERS[QUALITY_TIERS.indexOf(this.tier) + 1]!;
    this.warmup = 1; // let the new tier settle before judging it
    return this.tier;
  }

  /** Restarts measuring (e.g. after the tab was hidden). */
  reset(warmupS = 1): void {
    this.count = 0;
    this.elapsed = 0;
    this.warmup = warmupS;
  }
}

/** Median of the first `n` values; sorts that part of the array in place. */
export function medianInPlace(values: Float32Array, n: number): number {
  if (n <= 0) return 0;
  const part = values.subarray(0, n).sort();
  return n % 2 ? part[(n - 1) / 2]! : (part[n / 2 - 1]! + part[n / 2]!) / 2;
}
